import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { CardButton } from '@/src/elements/button/card/CardButton'
import { Button } from '@/src/elements/button/default'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { useCommunityJoinSteps } from '@/src/features/community/ui/hooks/useCommunityJoinSteps'
import { useFraternityOnboardingMutation } from '@/src/features/fraternity/queries/useFraternityOnboardingMutation'
import { useFraternityOnboardingQuery } from '@/src/features/fraternity/queries/useFraternityOnboardingQuery'
import { FraternityOnboardingWrapper } from '@/src/features/fraternity/ui/components/FraternityOnboardingWrapper'
import { getMeLocation } from '@/src/features/settings/utils/getMeLocation'
import { GENDER } from '@/src/features/user/models/user.enum'
import { ICON } from '@/src/general/enums/icons'
import { useTranslate } from '@/src/lang/useTranslate'

type TFraternityRecapScreenProps = TIdSlugParam & TSearchParamsParam

const getGenderLabel = (gender: GENDER | null | undefined, t: ReturnType<typeof useTranslate>['t']) => {
  if (gender === GENDER.F) return t('words.a-woman')
  if (gender === GENDER.M) return t('words.a-man')
  return '?'
}

const getPlaceLabel = (
  hasAskedRemote: boolean | null | undefined,
  city: string | undefined,
  t: ReturnType<typeof useTranslate>['t'],
) => {
  if (hasAskedRemote === true) return t('fraternity.place.remote')
  if (hasAskedRemote === false) return city ?? t('fraternity.place.nearby')
  return '?'
}

const getLeadLabel = (hasAskedLead: boolean | null | undefined, t: ReturnType<typeof useTranslate>['t']) => {
  if (hasAskedLead === true) return t('fraternity.lead.accept')
  if (hasAskedLead === false) return t('fraternity.lead.decline')
  return '?'
}

export const FraternityRecapScreen = ({ idSlug, searchParams }: TFraternityRecapScreenProps): ReactNode => {
  const { t } = useTranslate()
  const { data: me } = useMeQuery()
  const { data: onboarding } = useFraternityOnboardingQuery({ enabled: true })
  const joinSteps = useCommunityJoinSteps({ idSlug, searchParams, stepName: 'recap' })
  const { goToClose, navigateToJoinStep } = joinSteps
  const { mutateAsync, isPending: isSaving } = useFraternityOnboardingMutation()

  const handleSubmit = async () => {
    await mutateAsync({ requested_at: new Date().toISOString() })
    goToClose()
  }

  return (
    <FraternityOnboardingWrapper
      joinSteps={joinSteps}
      currentStep="recap"
      title={<FormattedMessage id="fraternity.recap.title" />}
      subtitle={<FormattedMessage id="fraternity.recap.subtitle" />}
      footer={
        <Button colors="primary" variant="solid" fullWidth isLoading={isSaving} onPress={handleSubmit}>
          <FormattedMessage id="fraternity.recap.cta" />
        </Button>
      }
    >
      <View className="gap-2">
        {me?.gender && (
          <CardButton
            startIcon={me.gender === GENDER.F ? ICON.FEMALE : ICON.MALE}
            endIcon={ICON.EDIT}
            classNames={{ iconRight: 'text-primary-icon-contrast-lower', content: 'flex-col-reverse' }}
            label={getGenderLabel(me?.gender, t)}
            description={t('fraternity.recap.sex')}
            onPress={() => navigateToJoinStep('gender', { fromRecap: true })}
          />
        )}
        <CardButton
          startIcon={ICON.MARKER_PIN}
          endIcon={ICON.EDIT}
          classNames={{ iconRight: 'text-primary-icon-contrast-lower', content: 'flex-col-reverse' }}
          label={getPlaceLabel(onboarding?.has_asked_remote, getMeLocation(me)?.city, t)}
          description={t('fraternity.recap.place')}
          onPress={() => navigateToJoinStep('place', { fromRecap: true })}
        />
        <CardButton
          startIcon={ICON.STAR_OFF}
          endIcon={ICON.EDIT}
          classNames={{ iconRight: 'text-primary-icon-contrast-lower', content: 'flex-col-reverse' }}
          label={getLeadLabel(onboarding?.has_asked_lead, t)}
          description={t('fraternity.recap.lead')}
          onPress={() => navigateToJoinStep('lead', { fromRecap: true })}
        />
      </View>
    </FraternityOnboardingWrapper>
  )
}
